import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Vote, Lightbulb, Zap, BarChart3, BookOpen, CheckCircle2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import FlashCard from "@/components/FlashCard";
import { Notice } from "@/components/Notices";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://customer-assets-gfyr7b9c.emergentagent.net/job_page-inspector-21/artifacts/r4m6h0nz_1F9934F9-A251-409A-95D1-F2D7E1E0D45C.png";

const MODULES = [
  { to: "/relampago", icon: Zap, title: "Relámpago", desc: "Una pregunta, 24 horas. Responde en segundos." },
  { to: "/encuestas", icon: Vote, title: "Encuestas", desc: "Participa en consultas con contexto neutral y fuentes." },
  { to: "/resultados", icon: BarChart3, title: "Resultados", desc: "Lo que respondió la ciudadanía, con su metodología." },
  { to: "/propuestas", icon: Lightbulb, title: "Propuestas", desc: "De la opinión a soluciones públicas concretas." },
  { to: "/metodologia", icon: BookOpen, title: "Metodología", desc: "Cómo hacemos las consultas y qué significan." },
];

export default function Home() {
  const [cons, setCons] = useState([]);
  const [stats, setStats] = useState(null);
  const [flash, setFlash] = useState(null);
  const { user, login } = useAuth();

  useEffect(() => {
    api.get("/consultations", { params: { featured: true } }).then((r) => setCons(r.data.slice(0, 2))).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/flash/today").then((r) => setFlash(r.data)).catch(() => {});
  }, []);

  const statItems = stats ? [
    { n: stats.consultations, l: "Encuestas activas" },
    { n: stats.proposals, l: "Propuestas ciudadanas" },
    { n: stats.votes, l: "Participaciones" },
  ].filter((s) => s.n > 0) : [];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-slate-900 overflow-hidden">
        <img src={HERO_IMG} alt="Ciudadanía conversando en una plaza al atardecer" className="absolute inset-0 w-full h-full object-cover object-right opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-cyan-400 text-xs font-semibold tracking-[0.2em] uppercase mb-6">
              <CheckCircle2 className="w-4 h-4" /> Plataforma ciudadana independiente
            </span>
            <h1 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[0.95]">
              Tu voz <span className="text-cyan-400">después</span> de votar.
            </h1>
            <p className="text-slate-300 text-lg md:text-xl mt-6 leading-relaxed max-w-2xl">
              Participa en encuestas, conoce lo que opinan otros ciudadanos y presenta soluciones para mejorar tu comunidad y tu país.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/relampago" data-testid="hero-cta-relampago"
                className="inline-flex items-center gap-2 bg-rose-500 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-rose-600 transition-colors">
                Responder la encuesta de hoy <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/propuestas" data-testid="hero-cta-propuestas"
                className="inline-flex items-center gap-2 bg-white/10 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/20 transition-colors border border-white/20">
                Explorar propuestas
              </Link>
            </div>
            <p className="flex items-center gap-2 text-slate-400 text-sm mt-6">
              <ShieldCheck className="w-4 h-4 text-cyan-400" /> Una persona. Una participación. Identidad protegida. Resultados transparentes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* INDEPENDENCE NOTICE */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Notice variant="general" />
        </div>
      </section>

      {/* STATS (only when there is participation) */}
      {statItems.length > 0 && (
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-3 gap-6">
            {statItems.map((s, i) => (
              <div key={s.l} className="text-center md:text-left" data-testid={`stat-${i}`}>
                <p className="font-heading font-black text-3xl md:text-4xl text-slate-900">{s.n}</p>
                <p className="text-sm text-slate-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FLASH POLL OF THE DAY */}
      {flash && (
        <section className="max-w-3xl mx-auto px-6 pt-14 -mb-2">
          <div className="flex items-center justify-between mb-4">
            <span className="inline-flex items-center gap-2 text-rose-500 text-xs font-bold tracking-[0.15em] uppercase">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> Encuesta Relámpago de hoy
            </span>
            <Link to="/relampago" className="text-sm font-semibold text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-1">Ver Relámpago <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <FlashCard poll={flash} />
        </section>
      )}

      {/* MODULES */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <p className="text-xs tracking-[0.2em] uppercase text-cyan-600 font-semibold mb-3">Cómo funciona 2Voto</p>
        <h2 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight mb-10">Infórmate. Participa. Propón.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {MODULES.map((m, i) => (
            <motion.div key={m.to} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link to={m.to} data-testid={`module-${m.to.slice(1)}`}
                className="group block bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-200 h-full">
                <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center mb-4 group-hover:bg-cyan-500 transition-colors">
                  <m.icon className="w-5 h-5 text-cyan-400 group-hover:text-white" />
                </div>
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">{m.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{m.desc}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED CONSULTATIONS */}
      {cons.length > 0 && (
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-cyan-600 font-semibold mb-2">Encuestas</p>
                <h2 className="font-heading font-black text-3xl text-slate-900 tracking-tight">Encuestas destacadas</h2>
              </div>
              <Link to="/encuestas" className="text-sm font-semibold text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-1">Ver todas <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {cons.map((c) => (
                <Link key={c.id} to={`/consultas/${c.id}`} data-testid={`home-consultation-${c.id}`}
                  className="block bg-slate-50 rounded-lg border border-slate-200 p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-200">
                  {c.category && <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">{c.category}</span>}
                  <h3 className="font-heading font-bold text-xl text-slate-900 mt-2 mb-3">{c.title}</h3>
                  <p className="text-slate-600 text-sm line-clamp-3">{c.context}</p>
                  <p className="text-xs text-slate-400 mt-4">{c.total_votes} participaciones</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading font-black text-3xl md:text-4xl text-white tracking-tight">Una pregunta. Miles de voces. Resultados transparentes.</h2>
          <p className="text-slate-300 mt-4 max-w-2xl mx-auto">2Voto es una plataforma ciudadana independiente para conocer opiniones, compartir soluciones y promover una participación pública mejor informada.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            {!user && (
              <button onClick={login} data-testid="cta-crear-cuenta"
                className="inline-flex items-center gap-2 bg-cyan-500 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-cyan-600 transition-colors">
                Crear cuenta
              </button>
            )}
            <Link to="/sobre" className="inline-flex items-center gap-2 bg-white/10 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/20 transition-colors border border-white/20">
              Sobre 2Voto <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
